import { useAuth } from '../contexts/AuthContext';
import { useEmpresa } from '../contexts/EmpresaContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { ArrowRightOnRectangleIcon, BuildingOfficeIcon, HomeIcon, HeartIcon } from '@heroicons/react/24/outline';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { formatEmpresaDisplayName } from '../utils/formatters';

export default function Header() {
  const { user, logout } = useAuth();
  const { empresa, empresas, setEmpresa, isIntegracoes } = useEmpresa();
  const { hasModulo, hasFeature } = useFeatureAccess();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleEmpresaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const empresaId = Number(e.target.value);
    const selectedEmpresa = empresas.find(emp => emp.id === empresaId);
    if (selectedEmpresa) {
      setEmpresa(selectedEmpresa);
      // Redirecionar para página inicial ao trocar empresa
      navigate('/');
    }
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 z-40 flex-shrink-0 sticky top-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-6">
            <Link to="/" className="flex items-center space-x-2 group" title="Ir para a Página Inicial">
              <img 
                src="/image/sst_logo.svg" 
                alt="SST Exames" 
                className="h-8 w-auto group-hover:opacity-90 transition-opacity"
              />
            </Link>
            <Link
              to="/"
              className={`hidden sm:flex items-center space-x-1 text-sm font-medium transition-colors ${
                location.pathname === '/'
                  ? 'text-primary-600 font-semibold'
                  : 'text-gray-500 hover:text-primary-600'
              }`}
            >
              <HomeIcon className="w-4 h-4" />
              <span>Painel</span>
            </Link>
            {!isIntegracoes && (
              <Link
                to="/organizacao"
                className={`hidden sm:flex items-center space-x-1 text-sm font-medium transition-colors ${
                  location.pathname.startsWith('/organizacao')
                    ? 'text-primary-600 font-semibold'
                    : 'text-gray-500 hover:text-primary-600'
                }`}
              >
                <BuildingOfficeIcon className="w-4 h-4" />
                <span>Organização</span>
              </Link>
            )}
            {!isIntegracoes &&
              (hasModulo('saude_corporativa') ||
                hasFeature('prontuario_clinico')) && (
                <Link
                  to="/saude/dashboard"
                  className={`hidden sm:flex items-center space-x-1 text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/saude/dashboard')
                      ? 'text-primary-600 font-semibold'
                      : 'text-gray-500 hover:text-primary-600'
                  }`}
                >
                  <HeartIcon className="w-4 h-4" />
                  <span>Saúde Corporativa</span>
                </Link>
              )}
          </div>
          <div className="flex items-center space-x-2 sm:space-x-4">
            {empresas.length > 1 && (
              <div data-tour="select-empresa" className="flex items-center space-x-1.5 sm:space-x-2 bg-gray-50 border border-gray-200/80 rounded-lg px-2 py-0.5">
                <BuildingOfficeIcon className="h-5 w-5 text-gray-500 shrink-0" />
                <select
                  value={empresa?.id || ''}
                  onChange={handleEmpresaChange}
                  className="text-xs sm:text-sm border-0 bg-transparent text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded px-1 py-1 max-w-[140px] sm:max-w-xs md:max-w-sm truncate cursor-pointer font-medium"
                  title={formatEmpresaDisplayName(empresa)}
                >
                  {empresas.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {formatEmpresaDisplayName(emp)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {empresa && empresas.length === 1 && (
              <div className="flex items-center space-x-1.5 sm:space-x-2 text-xs sm:text-sm text-gray-600 max-w-[140px] sm:max-w-none truncate bg-gray-50 border border-gray-200/80 rounded-lg px-2.5 py-1" title={formatEmpresaDisplayName(empresa)}>
                <BuildingOfficeIcon className="h-5 w-5 shrink-0 text-emerald-600" />
                <span className="truncate font-medium">{formatEmpresaDisplayName(empresa)}</span>
              </div>
            )}
            <span className="hidden sm:inline text-sm text-gray-700 font-medium">
              {user?.name}
            </span>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-1 sm:space-x-2 text-gray-700 hover:text-primary-500 transition-colors p-1 sm:p-0"
              title="Sair do Sistema"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
              <span className="hidden sm:inline">Sair</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
